import * as React from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Switch, TextField} from "@mui/material";
import {useTranslation} from "react-i18next";
import {BranchFormErrors} from "./AddBranchDialog";

export interface EditBranchDialogProps {
    open: boolean;
    editCode: string;
    setEditCode: (value: string) => void;
    editName: string;
    setEditName: (value: string) => void;
    editCity: string;
    setEditCity: (value: string) => void;
    editRegion: string;
    setEditRegion: (value: string) => void;
    editIsActive: boolean;
    setEditIsActive: (value: boolean) => void;
    editFormErrors: BranchFormErrors;
    setEditFormErrors: React.Dispatch<React.SetStateAction<BranchFormErrors>>;
    onClose: () => void;
    onSave: () => void;
}

export default function EditBranchDialog({
    open,
    editCode,
    setEditCode,
    editName,
    setEditName,
    editCity,
    setEditCity,
    editRegion,
    setEditRegion,
    editIsActive,
    setEditIsActive,
    editFormErrors,
    setEditFormErrors,
    onClose,
    onSave,
}: Readonly<EditBranchDialogProps>): React.ReactElement {
    const {t} = useTranslation();
    return (
        <Dialog
            open={open}
            onClose={(_event, reason) => {
                if (reason === "backdropClick") return;
                onClose();
            }}
            disableEscapeKeyDown
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>{t('hr.branches.dialog.edit.title')}</DialogTitle>
            <DialogContent>
                <Box sx={{display: "flex", flexDirection: "column", gap: 2, mt: 1}}>
                    <TextField
                        label={t('hr.branches.table.header.code')}
                        fullWidth
                        size="small"
                        required
                        value={editCode}
                        onChange={(e) => {
                            setEditCode(e.target.value);
                            setEditFormErrors((p) => ({...p, code: undefined}));
                        }}
                        error={!!editFormErrors.code}
                        helperText={editFormErrors.code}
                    />
                    <TextField
                        label={t('hr.branches.table.header.name')}
                        fullWidth
                        size="small"
                        required
                        value={editName}
                        onChange={(e) => {
                            setEditName(e.target.value);
                            setEditFormErrors((p) => ({...p, name: undefined}));
                        }}
                        error={!!editFormErrors.name}
                        helperText={editFormErrors.name}
                    />
                    <TextField
                        label={t('hr.branches.table.header.city')}
                        fullWidth
                        size="small"
                        required
                        value={editCity}
                        onChange={(e) => {
                            setEditCity(e.target.value);
                            setEditFormErrors((p) => ({...p, city: undefined}));
                        }}
                        error={!!editFormErrors.city}
                        helperText={editFormErrors.city}
                    />
                    <TextField
                        label={t('hr.branches.table.header.region')}
                        fullWidth
                        size="small"
                        required
                        value={editRegion}
                        onChange={(e) => {
                            setEditRegion(e.target.value);
                            setEditFormErrors((p) => ({...p, region: undefined}));
                        }}
                        error={!!editFormErrors.region}
                        helperText={editFormErrors.region}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={editIsActive}
                                onChange={(e) => setEditIsActive(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={t('common.status.active')}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
                <Button variant="contained" onClick={onSave}>{t('common.actions.save')}</Button>
            </DialogActions>
        </Dialog>
    );
}
